import { queryOptions } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ReportJobStatus } from "../../../shared/interfaces/JobStatus";
import { getJobStatus, type JobStatusSnapshot } from "./jobStatusApi";

export const jobKeys = {
  all: ["jobs"] as const,
  statuses: () => [...jobKeys.all, "status"] as const,
  status: (jobId: string) => [...jobKeys.statuses(), jobId] as const,
};

export const JOB_STATUS_POLL_MS = 3000;

/**
 * Grace window after a human-input resume (markJobResumed). The backend can
 * still report AWAITING_* for a beat after /receive accepts the resume, before
 * the graph actually picks it up. Within this window we treat that stale
 * AWAITING_* as "still transitioning" — keep polling and show loading — instead
 * of bouncing the user back to the input CTA they just acted on.
 */
export const RESUME_GRACE_MS = 30_000;

/** True while a just-resumed job is inside its transition grace window. */
export function isWithinResumeGrace(resumedAt: number | undefined): boolean {
  return resumedAt !== undefined && Date.now() - resumedAt < RESUME_GRACE_MS;
}

/**
 * Pure poll predicate (the templateJobPollInterval pattern).
 *
 * Stops on AWAITING_INPUT (paused for human input — the fan-out's
 * focus/visibility checks detect resumption elsewhere), terminal statuses,
 * and hard HTTP errors (the fan-out marks those FAILED).
 * Keeps polling through network blips — unlike the old setInterval loop,
 * which failed the job on any error.
 *
 * Exception: a just-resumed job (resumedAt inside the grace window) keeps
 * polling through a stale AWAITING_* snapshot so we catch the flip to a
 * running status instead of idling on soon-to-be-stale data.
 */
export function jobStatusPollInterval(
  data: JobStatusSnapshot | undefined,
  error: unknown,
  resumedAt?: number,
): number | false {
  if (error) {
    return isAxiosError(error) && error.response ? false : JOB_STATUS_POLL_MS;
  }
  const status = data?.jobStatus;
  const awaitingHuman =
    status === ReportJobStatus.AWAITING_INPUT ||
    status === ReportJobStatus.AWAITING_TRANSCRIPT_DECISION;
  if (awaitingHuman && isWithinResumeGrace(resumedAt)) {
    return JOB_STATUS_POLL_MS;
  }
  if (
    awaitingHuman ||
    status === ReportJobStatus.PENDING_REVIEW ||
    status === ReportJobStatus.FAILED
  ) {
    return false;
  }
  return JOB_STATUS_POLL_MS;
}

export function jobStatusQuery(jobId: string, opts?: { resumedAt?: number }) {
  return queryOptions<JobStatusSnapshot>({
    queryKey: jobKeys.status(jobId),
    queryFn: ({ signal }) => getJobStatus(jobId, signal),
    // The predicate owns ride-through-blips; query retries would only delay
    // hard-failure feedback.
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: (query) =>
      jobStatusPollInterval(query.state.data, query.state.error, opts?.resumedAt),
    // The old setInterval ignored tab visibility.
    refetchIntervalInBackground: true,
    // Covers the tab-hidden→visible case (v5's focusManager listens to
    // visibilitychange only). OS window refocus with the tab visible is
    // handled by useActiveJobStatusSync's window-focus listener.
    refetchOnWindowFocus: true,
  });
}
